import React from 'react';
import { Box, Button, Stack, Tooltip } from '@mui/material';
import {
  ArrowBack,
  ArrowForward,
  SkipNext as SkipIcon,
} from '@mui/icons-material';

/**
 * Reusable top/bottom navigation bar for wizard steps.
 *
 * Props:
 *   onBack          – handler for Back button (omit to hide)
 *   onNext          – handler for Continue/Finish button (omit to hide)
 *   onSkip          – handler for Skip button (omit to hide)
 *   backLabel       – label for Back button (default "Back")
 *   nextLabel       – label for Continue button (default "Continue")
 *   skipLabel       – label for Skip button (default "Skip")
 *   nextDisabled    – disable Continue button
 *   backDisabled    – disable Back button
 *   nextVariant     – MUI variant for next button (default "contained")
 *   nextColor       – MUI color for next button (default "primary")
 *   nextIcon        – override end icon for next button
 *   extraButtons    – additional JSX inserted between Skip and Continue
 *   sx              – extra sx passed to outer Box
 */
const WizardNavBar = ({
  onBack,
  onNext,
  onSkip,
  backLabel = 'Back',
  nextLabel = 'Continue',
  skipLabel = 'Skip',
  nextDisabled = false,
  backDisabled = false,
  nextVariant = 'contained',
  nextColor = 'primary',
  nextIcon,
  extraButtons,
  sx,
}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 1,
        ...sx,
      }}
    >
      {/* Left side — Back */}
      <Box>
        {onBack ? (
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={onBack}
            disabled={backDisabled}
          >
            {backLabel}
          </Button>
        ) : (
          <Box /> /* spacer */
        )}
      </Box>

      {/* Right side — Skip + extras + Continue */}
      <Stack direction="row" spacing={1} alignItems="center">
        {onSkip && (
          <Tooltip title={skipLabel}>
            <Button
              variant="outlined"
              color="warning"
              startIcon={<SkipIcon />}
              onClick={onSkip}
            >
              {skipLabel}
            </Button>
          </Tooltip>
        )}
        {extraButtons}
        {onNext && (
          <Button
            variant={nextVariant}
            color={nextColor}
            size="large"
            endIcon={nextIcon !== undefined ? nextIcon : <ArrowForward />}
            onClick={onNext}
            disabled={nextDisabled}
            sx={{ minWidth: 160 }}
          >
            {nextLabel}
          </Button>
        )}
      </Stack>
    </Box>
  );
};

export default WizardNavBar;
